import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Clock, Home as HomeIcon, PlayCircle, ShieldAlert } from "lucide-react";
import { NovaOrb } from "../components/NovaOrb";
import { BottomNav } from "../components/BottomNav";
import { api } from "../lib/api";

const GOAL_LABEL = {
  lose_weight: "lose weight",
  build_strength: "build strength",
  get_fit: "get generally fitter",
  improve_mobility: "move better",
  reduce_stress: "reduce stress",
};

const EQUIPMENT_LABEL = {
  home_none: "no equipment",
  home_dumbbells: "a few dumbbells at home",
  gym_machines: "gym machines",
  gym_free_weights: "full gym access",
};

const youtubeUrl = (query) => `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;

const ExerciseRow = ({ ex }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-white/[0.05] last:border-0" data-testid="exercise-row">
    <div className="flex-1">
      <p className="text-sm text-kaizen-text">{ex.name}</p>
      <p className="text-xs text-kaizen-dim mt-0.5">{ex.setup_steps}</p>
    </div>
    <div className="text-right shrink-0">
      <p className="text-xs text-kaizen-violetSoft">{ex.sets} × {ex.reps}</p>
      <p className="text-[10px] text-kaizen-dim2">{ex.rest_sec}s rest</p>
      <a
        href={youtubeUrl(ex.youtube_search_query)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="exercise-video-link"
        className="inline-flex items-center gap-1 text-[10px] text-kaizen-violetSoft/80 hover:text-kaizen-violetSoft mt-1"
      >
        <PlayCircle size={11} /> Watch form
      </a>
    </div>
  </div>
);

const SessionCard = ({ session, idx }) => {
  const [showFloor, setShowFloor] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className="glass-card rounded-3xl p-5"
      data-testid={`session-card-${idx}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-kaizen-dim">{session.day_label}</p>
          <p className="font-display text-lg text-kaizen-text mt-0.5">{session.muscle_focus}</p>
        </div>
        <button
          onClick={() => setShowFloor((v) => !v)}
          data-testid={`toggle-floor-variant-${idx}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border transition-colors duration-300 ${
            showFloor ? "border-kaizen-violet bg-kaizen-violet/15 text-kaizen-violetSoft" : "border-white/10 text-kaizen-dim"
          }`}
        >
          <HomeIcon size={11} /> 12-min floor
        </button>
      </div>

      {!showFloor ? (
        <div className="mt-4" data-testid="full-variant-list">
          {session.full_variant.exercises.map((ex, i) => (
            <ExerciseRow key={i} ex={ex} />
          ))}
        </div>
      ) : (
        <div className="mt-4" data-testid="floor-variant-list">
          <p className="font-display italic text-sm text-kaizen-violetSoft mb-2">
            "Can't make it today? 12 minutes at home keeps the chain alive."
          </p>
          {session.floor_variant.exercises.map((ex, i) => (
            <ExerciseRow key={i} ex={ex} />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default function PlanView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);

  useEffect(() => {
    api.get("/plan/current").then(({ data: d }) => {
      if (d.needs_onboarding) {
        navigate("/onboarding");
        return;
      }
      setData(d);
    });
  }, [navigate]);

  if (!data) {
    return (
      <div className="min-h-screen bg-kaizen-bg flex items-center justify-center">
        <NovaOrb size={90} />
      </div>
    );
  }

  const p = data.profile_snapshot || {};
  const goal = p.goal || {};

  return (
    <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="plan-page">
      <div className="absolute inset-0 -z-10 h-[320px] bg-[radial-gradient(circle_at_50%_0%,rgba(139,93,199,0.16),transparent_65%)]" />

      <div className="max-w-md mx-auto px-5 pt-8">
        <div className="flex flex-col items-center text-center">
          <NovaOrb size={90} />
          <p className="font-display text-2xl mt-4">Your Week 1 Plan</p>
          <p className="text-sm text-kaizen-dim mt-1 max-w-xs">
            Consistency over intensity — this week is your baseline, not your peak.
          </p>
        </div>

        {data.red_flag && (
          <div className="glass-card rounded-3xl p-5 mt-6 flex items-start gap-3" data-testid="red-flag-guidance-banner">
            <ShieldAlert size={18} className="text-ready-amber shrink-0 mt-0.5" />
            <p className="text-sm text-kaizen-dim leading-relaxed">
              For your safety, Week 1 focuses on walking and mobility only. Please train under professional
              supervision as you build back up.
            </p>
          </div>
        )}

        <div className="glass-card rounded-3xl mt-6 overflow-hidden" data-testid="why-this-plan-expander">
          <button
            onClick={() => setWhyOpen((v) => !v)}
            data-testid="why-this-plan-toggle"
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <span className="text-sm font-medium text-kaizen-violetSoft">Why this plan?</span>
            <ChevronDown size={16} className={`text-kaizen-dim transition-transform duration-300 ${whyOpen ? "rotate-180" : ""}`} />
          </button>
          {whyOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="px-5 pb-5 text-sm text-kaizen-dim leading-relaxed"
              data-testid="why-this-plan-content"
            >
              Built for a {p.age}-year-old aiming to {GOAL_LABEL[goal.type] || "get moving"}
              {goal.target_value ? ` (target: ${goal.target_value}${goal.target_unit || ""})` : ""} in {goal.timeframe_months} month
              {goal.timeframe_months === 1 ? "" : "s"}.{" "}
              {p.injuries?.length
                ? `Nothing here loads your ${p.injuries.join(" or ")}. `
                : "No injuries reported, so the full whitelist is in play. "}
              Using {EQUIPMENT_LABEL[p.equipment_tier] || "your equipment"}, {p.days_per_week} day
              {p.days_per_week === 1 ? "" : "s"} a week, at {p.experience?.replace("_", " ")} level.
            </motion.div>
          )}
        </div>

        <div className="flex items-center gap-1.5 mt-8 mb-3 text-kaizen-dim">
          <Clock size={13} />
          <p className="text-xs uppercase tracking-widest">This week's sessions</p>
        </div>

        <div className="space-y-4">
          {data.sessions.map((session, idx) => (
            <SessionCard key={idx} session={session} idx={idx} />
          ))}
        </div>

        <button
          onClick={() => navigate("/app/plan")}
          data-testid="continue-to-checkin-button"
          className="glow-cta mt-8 w-full py-3.5 rounded-full bg-kaizen-violet text-[#140A24] font-semibold text-sm hover:scale-[1.01] active:scale-[0.98] transition-transform duration-300"
        >
          Continue to today's check-in →
        </button>
      </div>

      <BottomNav />
    </div>
  );
}
